import { nanoid } from 'nanoid'

import type { Field, FieldType } from '../../question/models/Field'
import type { Answer } from '../models/Answer'

export const createAnswerText = (answer: Omit<Answer<'text'>, 'type'>): Answer<'text'> => {
  return {
    ...answer,
    type: 'text',
  }
}

export const createAnswerChoice = (answer: Omit<Answer<'choice'>, 'type'>): Answer<'choice'> => {
  return {
    ...answer,
    type: 'choice',
  }
}

export const createAnswerChoiceText = (
  answer: Omit<Answer<'choiceText'>, 'type'>,
): Answer<'choiceText'> => {
  return {
    ...answer,
    type: 'choiceText',
  }
}

export const createAnswerVideo = (answer: Omit<Answer<'video'>, 'type'>): Answer<'video'> => {
  return {
    ...answer,
    type: 'video',
  }
}

export const createAnswerNps = (answer: Omit<Answer<'nps'>, 'type'>): Answer<'nps'> => {
  return { ...answer, type: 'nps' }
}

export const createAnswerRanked = (answer: Omit<Answer<'ranked'>, 'type'>): Answer<'ranked'> => ({
  ...answer,
  type: 'ranked',
})

export const createAnswerMaxDiffSet = (
  answer: Omit<Answer<'maxDiffSet'>, 'type'>,
): Answer<'maxDiffSet'> => ({
  ...answer,
  type: 'maxDiffSet',
})

export const createAnswerSlider = (answer: Omit<Answer<'slider'>, 'type'>): Answer<'slider'> => ({
  ...answer,
  type: 'slider',
})

export const createEmptyAnswer = <T extends FieldType>(
  field: Pick<Field<T>, 'type' | 'id'>,
): Answer => {
  switch (field.type) {
    case 'text': {
      return createAnswerText({
        fieldId: field.id,
        id: nanoid(),
        value: { textInput: '' },
      })
    }
    case 'video': {
      return createAnswerVideo({
        fieldId: field.id,
        id: nanoid(),
        value: { videoBlob: undefined },
      })
    }
    case 'choice':
    case 'special':
    case 'imageChoice': {
      return createAnswerChoice({
        fieldId: field.id,
        id: nanoid(),
        value: { selected: false },
      })
    }
    case 'choiceText': {
      return createAnswerChoiceText({
        fieldId: field.id,
        id: 'other',
        value: { selected: false, textInput: '' },
      })
    }
    case 'nps': {
      return createAnswerNps({ fieldId: field.id, id: 'nps', value: { rating: undefined } })
    }
    case 'ranked': {
      return createAnswerRanked({
        fieldId: field.id,
        id: nanoid(),
        value: { ranking: 0 },
      })
    }
    case 'maxDiffSetChoice': {
      return createAnswerMaxDiffSet({
        fieldId: field.id,
        id: nanoid(),
        value: { selectedForBest: false, selectedForWorst: false },
      })
    }
    // Starts undefined rather than at the midpoint: an untouched slider has no answer, which is
    // what the submit gate reads.
    case 'slider': {
      return createAnswerSlider({
        fieldId: field.id,
        id: nanoid(),
        value: { value: undefined },
      })
    }
  }
  throw new Error('Unrecognised field type - ' + field.type)
}
